import { captureException } from '@sentry/react'
import { type UseMutationOptions, useMutation } from '@tanstack/react-query'
import type { AxiosProgressEvent } from 'axios'
import { unknown } from 'zod'
import { errorHandler, parseAxiosPromise } from '@/helpers/utils'
import { MUTATION_KEYS } from '@/query-client'
import {
	type HttpUploadPhotoOptions,
	httpUploadPhoto,
	httpUploadPhotoConfirm
} from '../http/photoset.http'

export interface useUploadPhoto {
	photosetId: number
	onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
	options?: UseMutationOptions<
		unknown,
		unknown,
		{ payload: HttpUploadPhotoOptions['payload']; imageId: number }
	>
}

export const useUploadPhoto = ({
	photosetId,
	onUploadProgress,
	options
}: useUploadPhoto) => {
	return useMutation({
		mutationKey: [MUTATION_KEYS.CONFIRM_UPLOAD_PHOTO],
		mutationFn: ({ payload }) =>
			parseAxiosPromise({
				axiosPromise: httpUploadPhoto({
					payload,
					onUploadProgress
				}),
				responseShape: unknown(),
				onZodError: captureException
			}),
		...options,
		onError: (error, variables, onMutateResult, context) => {
			errorHandler(error)
			options?.onError?.(error, variables, onMutateResult, context)
		},
		onSuccess: async (data, variables, onMutateResult, context) => {
			await httpUploadPhotoConfirm({
				urlParams: {
					photosetId,
					imageId: variables.imageId
				}
			})

			options?.onSuccess?.(data, variables, onMutateResult, context)
		}
	})
}
